import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Box, Tabs, Tab, CircularProgress, Snackbar, Alert, Link, MenuItem } from '@mui/material';
import axios from 'axios';
import { useAuth } from './AuthContext';

const US_STATES = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
];

const LoginRegisterComponent = ({ onLoginSuccess, initialEmail = '', initialData = {}, initialTab = 1 }) => {
    const { login } = useAuth();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [formData, setFormData] = useState({
        email: initialEmail || '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
    });

    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    // Validation functions
    const validatePhone = (phone) => {
        return /^\d{10}$|^\d{3}-\d{3}-\d{4}$/.test(phone);
    };

    const validateZip = (zip) => {
        return /^\d{5}(-\d{4})?$/.test(zip);
    };

    const handlePhoneChange = (e) => {
        let value = e.target.value.replace(/[^\d-]/g, '');

        if (value.length === 10 && !value.includes('-')) {
            value = value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        }

        setFormData((prev) => ({
            ...prev,
            phone: value,
        }));

        setFieldErrors((prev) => ({
            ...prev,
            phone: validatePhone(value) ? '' : 'Please enter a valid 10-digit phone number',
        }));
    };

    // Handle zip input
    const handleZipChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 5);
        setFormData((prev) => ({
            ...prev,
            zip: value,
        }));

        setFieldErrors((prev) => ({
            ...prev,
            zip: validateZip(value) ? '' : 'Please enter a valid ZIP code (12345 or 12345-6789)',
        }));
    };

    useEffect(() => {
        if (initialEmail || Object.keys(initialData).length > 0) {
            setFormData((prev) => ({
                ...prev,
                email: initialEmail || prev.email,
                firstName: initialData.firstName || prev.firstName,
                lastName: initialData.lastName || prev.lastName,
                phone: initialData.phone || prev.phone,
                address: initialData.address || prev.address,
                city: initialData.city || prev.city,
                state: initialData.state || prev.state,
                zip: initialData.zip || prev.zip,
            }));
        }
    }, [initialEmail, initialData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setFieldErrors((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setFieldErrors({});
        setShowForgotPassword(false);
    };

    const showError = (message, field = null) => {
        if (field) {
            setFieldErrors((prev) => ({ ...prev, [field]: message }));
        } else {
            setSnackbarSeverity('error');
            setSnackbarMessage(message);
            setShowSnackbar(true);
        }
    };

    const parseErrorMessage = (error) => {
        const response = error.response?.data;

        // Handle detailed validation errors from backend
        if (response?.errors) {
            // Check each field for validation errors
            for (const [field, message] of Object.entries(response.errors)) {
                if (field === 'password') {
                    return { field, message: 'Issue with password.' };
                }
                return { field, message };
            }
        }

        // Handle validation error messages in the main message
        if (response?.message) {
            // Check for password-related errors
            if (response.message.includes('password') || response.message.includes('Password')) {
                return {
                    field: 'password',
                    message: 'Issue with password.',
                };
            }

            // Handle email already exists
            if (response.message.includes('Email already exists') || response.message.includes('already in use')) {
                return { field: 'email', message: 'This email is already registered' };
            }

            // Handle invalid email format
            if (response.message.includes('email')) {
                if (response.message.includes('valid')) {
                    return { field: 'email', message: 'Please enter a valid email address' };
                }
            }

            // If we have a message but couldn't categorize it, return it as a general error
            return { field: null, message: response.message };
        }

        // Default error message
        return {
            field: null,
            message: 'An unexpected error occurred. Please try again.',
        };
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFieldErrors({});
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
                email: formData.email,
                password: formData.password,
            });

            if (response.data.status === 'success' && response.data.token) {
                const userData = {
                    token: response.data.token,
                    id: response.data.user.id || response.data.user._id,
                    firstName: response.data.user.firstName,
                    lastName: response.data.user.lastName,
                    email: response.data.user.email,
                    isAdmin: response.data.user.isAdmin || false,
                };

                localStorage.setItem('token', userData.token);
                localStorage.setItem('user', JSON.stringify(userData));
                
                // Set flag for PSI modal display on subscriptions page
                sessionStorage.setItem('justLoggedIn', 'true');
                
                login(userData);
                onLoginSuccess(userData);
                setSnackbarSeverity('success');
                setSnackbarMessage('Login successful!');
                setShowSnackbar(true);
            }
        } catch (error) {
            const { field, message } = parseErrorMessage(error);
            showError(message, field);
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        const errors = {};

        // Validate all required fields
        if (!formData.firstName) errors.firstName = 'First name is required';
        if (!formData.lastName) errors.lastName = 'Last name is required';
        if (!formData.email) errors.email = 'Email is required';
        if (!formData.password) errors.password = 'Password is required';
        if (!formData.phone || !validatePhone(formData.phone)) errors.phone = 'Valid phone number is required';
        if (!formData.address) errors.address = 'Address is required';
        if (!formData.city) errors.city = 'City is required';
        if (!formData.state) errors.state = 'State is required';
        if (!formData.zip || !validateZip(formData.zip)) errors.zip = 'Valid ZIP code is required (12345 or 12345-6789)';

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setLoading(true);
        setFieldErrors({});

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/users/signup`, formData);
            if (response.data.status === 'success') {
                const userData = {
                    token: response.data.token,
                    ...response.data.data.user,
                };
                // Set flag for PSI modal display on subscriptions page
                sessionStorage.setItem('justLoggedIn', 'true');
                
                login(userData);
                onLoginSuccess(userData);
                setSnackbarMessage('Registration successful!');
                setSnackbarSeverity('success');
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error('Registration error:', error);
            const { field, message } = parseErrorMessage(error);
            if (field) {
                setFieldErrors((prev) => ({ ...prev, [field]: message }));
            } else {
                setSnackbarSeverity('error');
                setSnackbarMessage(message);
                setShowSnackbar(true);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!formData.email) {
            showError('Please enter your email address', 'email');
            return;
        }

        setLoading(true);
        setFieldErrors({});
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, { email: formData.email });

            setSnackbarSeverity('success');
            setSnackbarMessage('If an account exists with this email, you will receive password reset instructions.');
            setShowSnackbar(true);
            setShowForgotPassword(false);
        } catch (error) {
            const { field, message } = parseErrorMessage(error);
            showError(message || 'An error occurred while processing your request', field);
        } finally {
            setLoading(false);
        }
    };

    const getHelperText = (fieldName) => {
        return fieldErrors[fieldName] || ' '; // Empty space to maintain consistent spacing
    };

    // Common TextField styling
    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover fieldset': {
                borderColor: '#ff5e1c',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#ff5e1c',
                borderWidth: 2,
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#ff5e1c',
        },
    };

    return (
        <Box sx={{
            width: '100%',
            maxWidth: 500,
            margin: 'auto',
            padding: 3,
        }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: '#0a1229',
                        mb: 1,
                        fontSize: { xs: '1.75rem', sm: '2rem' }
                    }}
                >
                    Welcome!
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: '#666',
                        fontSize: '0.95rem'
                    }}
                >
                    {activeTab === 1 ? 'Create an account to complete your purchase' : 'Sign in to your account'}
                </Typography>
            </Box>

            {/* Custom Tab Design */}
            <Box sx={{
                display: 'flex',
                gap: 2,
                mb: 4,
                backgroundColor: '#f8f8f8',
                padding: 0.75,
                borderRadius: 2,
            }}>
                <Box
                    onClick={() => handleTabChange(null, 0)}
                    sx={{
                        flex: 1,
                        padding: '12px 20px',
                        textAlign: 'center',
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease',
                        backgroundColor: activeTab === 0 ? '#fff' : 'transparent',
                        color: activeTab === 0 ? '#ff5e1c' : '#666',
                        boxShadow: activeTab === 0 ? '0 2px 8px rgba(255, 94, 28, 0.15)' : 'none',
                        '&:hover': {
                            backgroundColor: activeTab === 0 ? '#fff' : 'rgba(255, 94, 28, 0.05)',
                        }
                    }}
                >
                    Login
                </Box>
                <Box
                    onClick={() => handleTabChange(null, 1)}
                    sx={{
                        flex: 1,
                        padding: '12px 20px',
                        textAlign: 'center',
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease',
                        backgroundColor: activeTab === 1 ? '#fff' : 'transparent',
                        color: activeTab === 1 ? '#ff5e1c' : '#666',
                        boxShadow: activeTab === 1 ? '0 2px 8px rgba(255, 94, 28, 0.15)' : 'none',
                        '&:hover': {
                            backgroundColor: activeTab === 1 ? '#fff' : 'rgba(255, 94, 28, 0.05)',
                        }
                    }}
                >
                    Register
                </Box>
            </Box>
            {activeTab === 0 && (
                <form onSubmit={handleLogin}>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.email}
                        helperText={getHelperText('email')}
                        sx={textFieldSx}
                    />
                    {!showForgotPassword && (
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                            error={!!fieldErrors.password}
                            helperText={getHelperText('password')}
                            sx={textFieldSx}
                        />
                    )}
                    {showForgotPassword ? (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
                                Enter your email address and we'll send you instructions to reset your password.
                            </Typography>
                            <Button
                                onClick={handleForgotPassword}
                                fullWidth
                                variant="contained"
                                sx={{
                                    mt: 3,
                                    mb: 2,
                                    py: 1.5,
                                    backgroundColor: '#ff5e1c',
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 12px rgba(255, 94, 28, 0.25)',
                                    '&:hover': {
                                        backgroundColor: '#e54e0c',
                                        boxShadow: '0 6px 16px rgba(255, 94, 28, 0.35)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                                disabled={loading || !formData.email}
                            >
                                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Send Reset Email'}
                            </Button>
                        </>
                    ) : (
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2,
                                py: 1.5,
                                backgroundColor: '#ff5e1c',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '1rem',
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: '0 4px 12px rgba(255, 94, 28, 0.25)',
                                '&:hover': {
                                    backgroundColor: '#e54e0c',
                                    boxShadow: '0 6px 16px rgba(255, 94, 28, 0.35)',
                                    transform: 'translateY(-1px)',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Login'}
                        </Button>
                    )}
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => setShowForgotPassword(!showForgotPassword)}
                            sx={{
                                color: '#ff5e1c',
                                textDecoration: 'none',
                                fontWeight: 500,
                                cursor: 'pointer',
                                '&:hover': {
                                    color: '#e54e0c',
                                    textDecoration: 'underline',
                                }
                            }}
                        >
                            {showForgotPassword ? 'Back to Login' : 'Forgot Password?'}
                        </Link>
                    </Box>
                </form>
            )}

            {activeTab === 1 && (
                <form onSubmit={handleRegister}>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.firstName}
                        helperText={getHelperText('firstName')}
                        sx={textFieldSx}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.lastName}
                        helperText={getHelperText('lastName')}
                        sx={textFieldSx}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.email}
                        helperText={getHelperText('email')}
                        sx={textFieldSx}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.password}
                        helperText={getHelperText('password')}
                        sx={textFieldSx}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.phone}
                        helperText={fieldErrors.phone || 'Format: 1234567890 or 123-456-7890'}
                        sx={textFieldSx}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.address}
                        helperText={getHelperText('address')}
                        sx={textFieldSx}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="City"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.city}
                        helperText={getHelperText('city')}
                        sx={textFieldSx}
                    />
                    <TextField
                        select
                        fullWidth
                        margin="normal"
                        label="State"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.state}
                        helperText={getHelperText('state')}
                        sx={textFieldSx}
                    >
                        {US_STATES.map((state) => (
                            <MenuItem key={state.value} value={state.value}>
                                {state.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="ZIP Code"
                        name="zip"
                        value={formData.zip}
                        onChange={handleZipChange}
                        required
                        disabled={loading}
                        error={!!fieldErrors.zip}
                        helperText={fieldErrors.zip || 'ZIP code (12345 or 12345-6789)'}
                        inputProps={{ maxLength: 5 }}
                        sx={textFieldSx}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{
                            mt: 3,
                            mb: 2,
                            py: 1.5,
                            backgroundColor: '#ff5e1c',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '1rem',
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: '0 4px 12px rgba(255, 94, 28, 0.25)',
                            '&:hover': {
                                backgroundColor: '#e54e0c',
                                boxShadow: '0 6px 16px rgba(255, 94, 28, 0.35)',
                                transform: 'translateY(-1px)',
                            },
                            '&:active': {
                                transform: 'translateY(0)',
                            },
                            transition: 'all 0.2s ease',
                        }}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Register'}
                    </Button>
                </form>
            )}

            <Snackbar
                open={showSnackbar}
                autoHideDuration={6000}
                onClose={() => setShowSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setShowSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }} elevation={6}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LoginRegisterComponent;
